import {
    ClaimApi,
    CollectionApi,
    CoreFetch,
    createRequestFilters,
    FilterMethod,
    GuarantorApi,
    GuarantorCommunication,
    ICustomFilter,
    Invoice,
    InvoiceApi,
    ListProviderClaimsOrderEnum,
    ListProviderCollectionPaymentsOrderEnum,
    ListProviderGuarantorsRequest,
    ProviderClaim,
    ProviderClaimSortType,
    ProviderCollectionPayment,
    ProviderCollectionPaymentSortType,
    ProviderGuarantor,
    ProviderPaymentPlan,
    SupportCreatePaymentPlanPayload,
    SupportShortUrl,
} from '@enter/core';
import { IItemDetail, IItemListing } from './root.store';
import { fetch, parseFetchListRaw, parseResponse } from './util';

export class GuarantorService {
    public static async getAllGuarantorClaims(id: string): Promise<ProviderClaim[]> {
        const offsetStep = 50;
        const limit = 50;
        let offset = 0;
        let result: ProviderClaim[] = [];
        let resTotal = 0;
        const filters: ICustomFilter[] = [
            {
                filterId: 'filterGuarantorId',
                filterMethod: FilterMethod.EQ,
                filterType: 'String',
                value: id,
            },
        ];

        const req = createRequestFilters(filters);
        do {
            const guarantorClaimsRaw = await CoreFetch<ClaimApi>(ClaimApi).listProviderClaimsRaw({
                ...req,
                offset,
                limit,
                sort: [ProviderClaimSortType.FROMDOS],
                order: [ListProviderClaimsOrderEnum.DESCENDING],
            });
            const { value, total } = await parseFetchListRaw(guarantorClaimsRaw);
            result = [...result, ...value];
            resTotal = total;
            offset += offsetStep;
        } while (result.length < resTotal);

        return result;
    }

    public static async getAllGuarantorPayments(id: string): Promise<ProviderCollectionPayment[]> {
        const offsetStep = 50;
        const limit = 50;
        let offset = 0;
        let result: ProviderCollectionPayment[] = [];
        let resTotal = 0;
        const filters: ICustomFilter[] = [
            {
                filterId: 'filterGuarantorId',
                filterMethod: FilterMethod.EQ,
                filterType: 'String',
                value: id,
            },
        ];

        const req = createRequestFilters(filters);
        do {
            const guarantorPayments = await CoreFetch<CollectionApi>(CollectionApi).listProviderCollectionPaymentsRaw({
                ...req,
                offset,
                limit,
                sort: [ProviderCollectionPaymentSortType.PAYMENTDATE],
                order: [ListProviderCollectionPaymentsOrderEnum.DESCENDING],
            });
            const { value, total } = await parseFetchListRaw(guarantorPayments);
            result = [...result, ...value];
            resTotal = total;
            offset += offsetStep;
        } while (result.length < resTotal);

        return result;
    }

    public static async getAllGuarantorInvoices(id: string): Promise<Invoice[]> {
        const offsetStep = 50;
        const limit = 50;
        let offset = 0;
        let result: Invoice[] = [];
        let resTotal = 0;
        const filters: ICustomFilter[] = [
            {
                filterId: 'filterGuarantorId',
                filterMethod: FilterMethod.EQ,
                filterType: 'String',
                value: id,
            },
        ];

        const req = createRequestFilters(filters);
        do {
            const res = await CoreFetch<InvoiceApi>(InvoiceApi).listProviderInvoicesRaw({
                ...req,
                offset,
                limit,
            });
            const { value, total } = await parseFetchListRaw(res);
            result = [...result, ...value];
            resTotal = total;
            offset += offsetStep;
        } while (result.length < resTotal);

        return result;
    }

    public static async getAllGuarantorPlans(id: string): Promise<ProviderPaymentPlan[]> {
        const offsetStep = 50;
        const limit = 50;
        let offset = 0;
        let result: ProviderCollectionPayment[] = [];
        let resTotal = 0;
        const filters: ICustomFilter[] = [
            {
                filterId: 'filterGuarantorId',
                filterMethod: FilterMethod.EQ,
                filterType: 'String',
                value: id,
            },
        ];

        const req = createRequestFilters(filters);
        do {
            const res = await CoreFetch<CollectionApi>(CollectionApi).listProviderCollectionPaymentPlansRaw({
                ...req,
                offset,
                limit,
            });
            const { value, total } = await parseFetchListRaw(res);
            result = [...result, ...value];
            resTotal = total;
            offset += offsetStep;
        } while (result.length < resTotal);

        return result;
    }

    public static async getAllGuarantorCommunications(id: string): Promise<GuarantorCommunication[]> {
        return CoreFetch<GuarantorApi>(GuarantorApi).listProviderGuarantorCommunications({
            id,
        });
    }

    public static async getCheckoutUrl(guarantorId: string): Promise<SupportShortUrl | undefined> {
        return fetch(guarantorId, id => CoreFetch<GuarantorApi>(GuarantorApi).getProviderGuarantorCheckoutUrl({ id }));
    }

    public static async createPaymentPlan(
        payload: SupportCreatePaymentPlanPayload,
    ): Promise<SupportShortUrl | undefined> {
        return CoreFetch<CollectionApi>(CollectionApi).postProviderPaymentPlan({
            supportCreatePaymentPlanPayload: payload,
        });
    }

    public async fetchGuarantorListing(
        lpgr: ListProviderGuarantorsRequest,
    ): Promise<IItemListing<ProviderGuarantor> | undefined> {
        const res = await fetch(lpgr, req => CoreFetch<GuarantorApi>(GuarantorApi).listProviderGuarantorsRaw(req));
        if (res) {
            return {
                items: await parseResponse(res),
                limit: Number(await res.raw.headers.get('pagination-limit')),
                offset: Number(await res.raw.headers.get('pagination-offset')),
                total: Number(await res.raw.headers.get('pagination-total')),
            };
        }
    }
    public async fetchGuarantor(id: string): Promise<ProviderGuarantor | undefined> {
        return fetch({ id }, req => CoreFetch<GuarantorApi>(GuarantorApi).getProviderGuarantor(req));
    }
    public async fetchGuarantorDetail(guarantorId: string): Promise<IItemDetail<ProviderGuarantor> | undefined> {
        const item = await fetch(guarantorId, id => CoreFetch<GuarantorApi>(GuarantorApi).getProviderGuarantor({ id }));
        if (item) {
            return {
                item,
            };
        }
    }
}
